import { Clock, Timer } from 'lucide-react';
import { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Button } from '@/components/ui/button';

interface AfterHoursHeroProps {
  product: any;
  activeWordIndex: number;
  rotatingWords: string[];
  onGetStarted: () => void;
}

// Hero carousel images (optimized)
const heroImages = [
  '/optimized/hero/replicate-prediction-3ymns27nhsrm80ctv0ptrjgqmw.webp',
  '/optimized/hero/tmp9o88qb_2.webp',
  '/optimized/hero/3yge95pg99rmt0cvv3m8f4009w_1768870726277 (1).webp',
  '/optimized/hero/ycxzdqd0vdrmw0cvv4er3yp0vw_1768874184816.webp',
];

// Ken Burns animation variants - each slide gets a random direction
const kenBurnsVariants = [
  { scale: [1, 1.04], x: [0, -8], y: [0, -5] },   // Zoom + pan top-left (subtle)
  { scale: [1, 1.05], x: [0, 8], y: [0, -4] },    // Zoom + pan top-right (subtle)
  { scale: [1, 1.03], x: [0, -5], y: [0, 6] },    // Zoom + pan bottom-left (subtle)
  { scale: [1, 1.04], x: [0, 6], y: [0, 4] },     // Zoom + pan bottom-right (subtle)
  { scale: [1, 1.05], x: [0, 0], y: [0, 0] },     // Center zoom only (subtle)
];

export const AfterHoursHero = ({
  product,
  activeWordIndex,
  rotatingWords,
  onGetStarted,
}: AfterHoursHeroProps) => {
  const currentWord = rotatingWords[activeWordIndex];
  const [imageIndex, setImageIndex] = useState(0);

  // Rotate hero images every 8 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 8000);
    return () => clearInterval(timer);
  }, []);

  return (
    <>
      <section className="relative w-full h-[65vh] sm:h-[78vh] md:h-[84vh] min-h-[480px] sm:min-h-[600px] md:min-h-[660px] flex items-center text-white overflow-hidden">
        {/* Background Image Carousel with Ken Burns + Crossfade */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <AnimatePresence mode="sync">
            <motion.img
              key={imageIndex}
              src={heroImages[imageIndex]}
              alt="After Hours hero background"
              className="absolute inset-0 w-full h-full object-cover"
              initial={{ opacity: 0, scale: 1 }}
              animate={{
                opacity: 1,
                scale: kenBurnsVariants[imageIndex % kenBurnsVariants.length].scale,
                x: kenBurnsVariants[imageIndex % kenBurnsVariants.length].x,
                y: kenBurnsVariants[imageIndex % kenBurnsVariants.length].y,
              }}
              exit={{ opacity: 0 }}
              transition={{
                opacity: { duration: 1.2, ease: 'easeInOut' },
                scale: { duration: 8, ease: 'linear' },
                x: { duration: 8, ease: 'linear' },
                y: { duration: 8, ease: 'linear' },
              }}
            />
          </AnimatePresence>
          <div className="absolute inset-0 bg-black/70 z-10" />
        </div>

        {/* Main content - Centered */}
        <div className="relative z-10 w-full flex items-center justify-center px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-5xl">
            {/* Tag */}
            <div className="mb-6 inline-block">
              <span className="px-4 py-2 text-sm sm:text-base font-semibold text-white bg-gradient-to-r from-purple-600 to-blue-600 rounded-full">
                After Hours
              </span>
            </div>

            <h1 className="font-bold leading-none drop-shadow-2xl mb-6" style={{ fontSize: 'clamp(2.5rem, 6vw, 7rem)' }}>
              <div className="text-white">
                Never miss {currentWord === 'opportunity' ? 'an' : 'a'}
              </div>
              <div className="whitespace-nowrap">
                <span
                  key={currentWord}
                  className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent animate-shimmer bg-[length:200%_100%]"
                  style={{
                    backgroundImage:
                      'linear-gradient(110deg, #60a5fa 0%, #a855f7 25%, #ec4899 50%, #a855f7 75%, #60a5fa 100%)',
                  }}
                >
                  {currentWord}
                </span>{' '}
                <span className="text-white">again</span>
              </div>
            </h1>

            <p
              className="mt-6 text-white font-normal drop-shadow-lg"
              style={{ fontSize: 'clamp(1.125rem, 1.75vw, 1.75rem)' }}
            >
              Intelligent after-hours call handling that captures leads, routes emergencies, and keeps customers happy while you rest
            </p>

            {/* CTAs */}
            <div className="mt-8">
              <Button
                size="lg"
                onClick={onGetStarted}
                className="rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold shadow-lg hover:from-purple-700 hover:to-blue-700 transition-all hover:-translate-y-0.5 touch-manipulation min-h-[48px] px-6 sm:px-8"
                style={{ fontSize: 'clamp(1rem, 1.2vw, 1.25rem)', padding: 'clamp(0.875rem, 1vw, 1rem) clamp(1.5rem, 2vw, 2rem)' }}
              >
                Get Set Up Now
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Add styles for shimmer animation */}
      <style>{`
        @keyframes shimmer {
          0% { background-position: 200% center; }
          100% { background-position: -200% center; }
        }
        .animate-shimmer {
          animation: shimmer 3s ease-in-out infinite;
        }
      `}</style>
    </>
  );
};
